import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FilterDropdown, {FilterDropdownRef} from './FilterDropdown';
import FilterLayout from './FilterLayout';
import FilterMultiSelect from './FilterMultiSelect';
import {FilterOption} from '../../../common/types/FilterOption';
import workshopsService from '../../services/workshopsService';
import {handleError} from '../../../common/utils/errorHandling';
import './filters.scss';

interface WorkshopsFilterProps {
  workshops?: string[];
  onFilterChange: (workshops?: string[]) => void;
}

const WorkshopsFilter: React.FC<WorkshopsFilterProps> = ({
  workshops = [],
  onFilterChange
}) => {
  const {t} = useTranslation();
  const [tempWorkshops, setTempWorkshops] = useState<string[]>(workshops);
  const [workshopOptions, setWorkshopOptions] = useState<FilterOption[]>([]);
  const filterDropdownRef = useRef<FilterDropdownRef>(null);

  const hasActiveFilter = workshops.length > 0;

  useEffect(() => {
    fetchWorkshops();
  }, []);

  const fetchWorkshops = async () => {
    handleError(async () => {
      const workshopsData = await workshopsService.getAllWorkshops();
      const options: FilterOption[] = workshopsData.map(workshop => ({
        label: workshop.location ? `${workshop.name} (${workshop.location})` : workshop.name,
        value: workshop.id
      }));
      setWorkshopOptions(options);
    }, 'Error fetching workshops');
  };

  const applyFilter = () => {
    const filteredWorkshops = tempWorkshops.length > 0 ? tempWorkshops : undefined;
    onFilterChange(filteredWorkshops);
  };

  const clearFilter = () => {
    setTempWorkshops([]);
    onFilterChange(undefined);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      applyFilter();
      filterDropdownRef.current?.close();
    } else if (event.key === 'Escape') {
      setTempWorkshops(workshops);
      filterDropdownRef.current?.close();
    }
  };

  const handleApplyClick = () => {
    applyFilter();
    filterDropdownRef.current?.close();
  };

  const getDisplayLabel = () => {
    if (workshops.length === 0) {
      return t('gallery.filters.workshops');
    }
    return `${t('gallery.filters.workshops')} | ${workshops.length}`;
  };

  return (
    <FilterDropdown
      ref={filterDropdownRef}
      label={getDisplayLabel()}
      hasActiveFilter={hasActiveFilter}
      onClearFilter={clearFilter}
    >
      <FilterLayout
        title={t('gallery.filters.filterByWorkshops')}
        onClear={clearFilter}
        onApply={handleApplyClick}
      >
        <FilterMultiSelect
          value={tempWorkshops}
          options={workshopOptions}
          onChange={setTempWorkshops}
          onKeyDown={handleKeyPress}
          width={600}
          showFilter={true}
        />
      </FilterLayout>
    </FilterDropdown>
  );
};

export default WorkshopsFilter;
